import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { ControlPoint, AccessTimeFilled } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { $crud } from "../../utils/CrudFactory";
import { usePagination } from "../../hooks";
import DynamicTable from "../../components/DynamicTable";
import SearchComponent from "../../components/Search";
import DashboardTable from "../../components/NewTable/DashboardTable";
import {
  orderFilters,
  regularOfferFilters,
  tutorialFilters,
} from "../../utils/filterConfig";
import {
  Visibility,
  VisibilityOff,
  EditNote,
  Delete,
} from "@mui/icons-material";
import TutorialVideoBox from "../../components/modals/TutorialVideoBox";
import { BASE_URL } from "../../constants";
import play from "../../assets/images/svg/play.svg";
import Filter from "../../components/Filter";
import { timetoUTC, toLocalTime } from "../../helper/functions";

export default function Tutorials() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openRegularOfferModal, setOpenRegularOfferModal] = useState(false);
  const [rows, setRows] = useState([]); //STATE TO GET THE ORDER LIST
  const [isFilter, setIsFilter] = useState(false); //STATE TO GET THE ORDER LIST
  const [isSorting, setIsSorting] = useState(false);

  const { page, limit, setLimit, setPage, total, setTotal } = usePagination(); // PAGINATION PROPS

  // STATE TO MANAGE FILTERS --------------------------------------
  const [filtrationData, setFiltrationData] = useState({
    from: "",
    to: "",
    search: "",
    visibility: [],
    archived: [],
    videoSourceType: [],
  });
  // STATE TO MANAGE FILTERS --------------------------------------
  async function retrieveData() {
    const isFilterApplied = Object.values(filtrationData).some(
      (value) => value !== "" && value !== false
    );

    const params = {
      search: filtrationData.search,
      from: timetoUTC(filtrationData.from),
      to: timetoUTC(filtrationData.to),
      visibility: filtrationData.visibility,
      archived: filtrationData.archived,
      videoSourceType: filtrationData.videoSourceType,
    };

    // Conditionally include limit and page if no filters are applied
    if (!isFilterApplied) {
      params.limit = 0;
      params.page = page;
    }

    const { data } = await $crud.retrieve("admin/tutorials", params);
    return data;
  }
  useEffect(() => {
    // RUNNING THE retrieveData() FUNCTION ---------------------------------
    const id = setTimeout(
      () => {
        retrieveData().then((res) => {
          setRows(res.result);
          setTotal(res.total);
        });
      },
      searchInput.length > 0 ? 300 : 0
    );

    return () => clearTimeout(id);
    // RUNNING THE retrieveData() FUNCTION ---------------------------------
  }, [limit, page, filtrationData]);

  const handleSearchInputChange = (value) => {
    if (value.trim() === "") {
      setFiltrationData((prevData) => ({
        ...prevData,
        search: "",
      }));
      setSearchInput("");
    }
    setSearchInput(value);
    setFiltrationData((prevData) => ({
      ...prevData,
      search: value,
    }));
  };

  // COLUMNS IN THE ORDER TABLE-----------------------------
  const clientColumns = [
    {
      id: "tutorialId",
      label: "Tutorial ID",
      align: "center",
    },

    {
      id: "title",
      label: "Video Title",
      align: "center",
    },
    {
      id: "createdAt",
      label: "Created Date",
      align: "center",
      renderCell: (row) => toLocalTime(row.createdAt),
    },
    {
      id: "videoSourceType",
      label: "Video Type",
      align: "center",
    },
    {
      id: "tags",
      label: "Tags",
      align: "center",
    },
    {
      id: "stage",
      label: "Stage",
      align: "center",
      renderCell: (row) => {
        const getStatusStyles = (status) => {
          switch (status) {
            case "Active":
              return {
                background: "rgba(7, 79, 36, 0.35)",
              };
            case "Archived":
              return {
                background: "rgba(104, 60, 140, 0.5)",
              };
            default:
              return {
                background: "#FFF0ED",
              };
          }
        };
        const styles = getStatusStyles(row.stage);
        return (
          <Button
            sx={{
              background: styles.background,
              cursor: "auto",
              borderRadius: "22px",
              color: "rgba(23, 26, 31, 1)",
              padding: "7px 27px",
              textTransform: "capitalize",
              border: "none",
              fontSize: "12px",
              width: "100px",
              outline: "none !important",
              "&:hover": {
                outline: styles.background,
                background: styles.background,
              },
              "&:focus": {
                outline: styles.background,
                background: styles.background,
              },
            }}
          >
            {row.stage}
          </Button>
        );
      },
    },
    {
      id: "visibility",
      label: "Visibility",
      align: "center",
      renderCell: (row) => {
        return (
          <Typography sx={{ fontSize: "12px" }}>
            {row.visibility.includes("Drivers") &&
            row.visibility.includes("Clients")
              ? row.visibility.includes("Drivers") &&
                row.visibility.startsWith("Drivers")
                ? "Drivers & Clients"
                : "Clients & Drivers"
              : row.visibility}
          </Typography>
        );
      },
    },
    {
      id: "action",
      label: "View",
      align: "center",
      renderCell: (row, navigate, handleExpand, expandedId) => {
        const isRowExpanded = expandedId === row._id;
        return (
          <>
            {isRowExpanded ? (
              <IconButton
                sx={{
                  fontSize: "8px",
                  color: "rgba(104, 60, 140, 1)",
                }}
                onClick={() => handleExpand(row._id)}
                // Add your onClick logic for delete here
              >
                <VisibilityOff />
              </IconButton>
            ) : (
              <IconButton
                sx={{ fontSize: "8px", color: "rgba(104, 60, 140, 1)" }}
                onClick={() => handleExpand(row._id)}
              >
                <Visibility />
              </IconButton>
            )}
          </>
        );
      },
    },
  ];
  // COLUMNS IN THE ORDER TABLE-----------------------------

  const handleExpandRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  // FUNCTION TO SET THE FILTERS IN filtrationData STATE -----------------------------
  const getFilteredData = (data) => {
    if (!data.visibility) data.visibility = "";
    if (!data.videoSourceType) data.videoSourceType = "";
    if (!data.search) data.search = "";
    if (!data.archived) data.archived = "";
    setFiltrationData({
      ...filtrationData,
      ...data,
    });
    setIsFilter(true);
    setPage(0);
  };
  // FUNCTION TO SET THE FILTERS IN filtrationData STATE -----------------------------
  return (
    <>
      <Grid container sx={{ padding: "0 18px" }}>
        <Grid item xs={12} md={6}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: "30px",
              color: "#673C8E", // Return the value directly
            }}
          >
            Tutorials
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ textAlign: "right", paddingRight: "24px" }}
        >
          <Button
            onClick={() => navigate("add")}
            variant="contained"
            size="small"
            sx={{
              padding: "10px 20px",
              textTransform: "capitalize",
              backgroundColor: "rgba(103, 60, 142, 1)",
              color: "#fff",
              borderRadius: "4px",
              fontWeight: 600,
              // fontSize: "16px",
              "&:hover": {
                backgroundColor: "rgba(103, 60, 142, 1)",
                color: "#fff",
              },
            }}
            startIcon={<ControlPoint />}
          >
            Create New Video
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ paddingRight: "24px" }}>
        {/* <DynamicTable
          rows={rows}
          type="tutorials"
          url="tutorials"
          setRows={setRows}
          searchPlaceholderData={searchInput}
          onSearchData={handleSearchInputChange}
          filterConfig={tutorialFilters} // Pass the filter checkbox options
          noRecordTitle="No tutorials yet !" // no data title
          onFilterData={getFilteredData} // filteration data
          onResetFilters={() => {
            setFiltrationData((prev) => ({
              search: prev.search,
              from: "",
              to: "",
              visibility: [],
              archived: [],
              videoSourceType: [],
            }));
            setIsFilter(false);
          }}
          isFilter={isFilter}
          filtrationData={filtrationData}
          paginationProps={{
            limit: 0,
            page,
            setLimit,
            setPage,
            total,
            setTotal,
          }}
          columns={clientColumns} // Pass the columns prop
          expandedRows={expandedRows}
          handleExpandRow={handleExpandRow}
        /> */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: "18px",
            paddingLeft: "18px",
            marginTop: "10px",
          }}
        >
          <Box sx={{ width: "50%" }}>
            {/* ============================== CALLING SEARCH COMPONENT ================================== */}
            <SearchComponent
              type="tutorials"
              setSortedData={setRows}
              onSearchData={handleSearchInputChange}
              setIsSorting={setIsSorting}
              padding="2px"
              borderRadius="8px"
              totalData={rows}
              placeholder="Search in this section"
              value={searchInput}
            />
          </Box>
          <Filter
            filtrationData={filtrationData}
            onFilterData={getFilteredData} // filteration data
            onResetFilters={() => {
              setFiltrationData((prev) => ({
                search: prev.search,
                from: "",
                to: "",
                visibility: [],
                archived: [],
                videoSourceType: [],
              }));
              setIsFilter(false);
            }}
            type="tutorials"
            url="tutorials"
            config={tutorialFilters}
          />
        </div>
        <DashboardTable
          columns={clientColumns}
          rows={rows}
          emptyMessage="No tutorials yet !"
          renderExpandedRow={(row) => {
            return (
              <Box
                sx={{
                  position: "relative",
                  padding: "16px 34px",
                  paddingBottom: 0,
                  gap: "20px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20px",
                    right: "60px",
                  }}
                >
                  <Button
                    onClick={() => navigate(`add?id=${row._id}`)}
                    variant="contained"
                    size="small"
                    sx={{
                      padding: "8px 36px",
                      textTransform: "capitalize",
                      backgroundColor: "rgba(104, 60, 140, 0.35)",
                      color: "rgba(103, 60, 142, 1)",
                      borderRadius: "20px",
                      "&:hover": {
                        backgroundColor: "rgba(104, 60, 140, 0.35)",
                        color: "rgba(103, 60, 142, 1)",
                      },
                    }}
                    endIcon={<EditNote />}
                  >
                    Edit
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    justifyContent: "space-between",
                    gap: "20px",
                    width: "70%",
                  }}
                >
                  <TutorialVideoBox
                    key={row._id}
                    row={row}
                    BASE_URL={BASE_URL}
                    playIcon={play}
                  />

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "18px",
                        color: "#673C8E",
                        fontWeight: 700,
                      }}
                    >
                      Description
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#000",
                      }}
                    >
                      {row.description}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "18px",
                        color: "#673C8E",
                        fontWeight: 700,
                      }}
                    >
                      Duration
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#000",
                      }}
                    >
                      {row.duration}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          }}
        />
      </Box>
    </>
  );
}
